import type { ObjectGraph } from '@jet/environment/dependencies/object-graph';
import type { Opt } from '@jet/environment/types/optional';
import { Bag } from './bag';
import { ObjectGraphType } from './object-graph-type';

export interface UnnormalizedLocale {
    storefront: Opt<string>;
    language: Opt<string>;
}

export interface NormalizedLocale {
    storefront: string;
    // If null, then the language is the storefront's default
    language: Opt<string>;
}

export interface Locale {
    /**
     * Given a storefront and language (a "locale") from a URL, normalize them
     * to a valid storefront (falling back to a default) and a supported
     * language in that storefront (falling back to the default for that
     * storefront). If the normalized language is the default,
     * `language: null` is returned.
     */
    normalize(unnormalizedLocale: UnnormalizedLocale): NormalizedLocale;
}

export function normalizeLocale(
    unnormalizedLocale: UnnormalizedLocale,
    objectGraph: ObjectGraph,
): NormalizedLocale {
    const locale = objectGraph.optional(ObjectGraphType.locale);

    if (locale) {
        return locale.normalize(unnormalizedLocale);
    }

    // This dependency is optional. When provided, the returned value is be
    // used to annotate intents with (normalized) data from the URL (the
    // storefront and language) when routing a URL.
    //
    // When not provided, we use a reasonable fallback. Particularly, we assume
    // that all intents for the app are for the bag's storefront and default
    // language. This default should be correct if consulted, but there
    // currently is a light assumption that this data is only relied upon by
    // hosts that provide the dependency.
    //
    // Should hosts in the future need to extract storefront and language from
    // routed URLs, they can provide this dependency that the annotated data
    // will flow through this normalizer and then get added to
    // `RouteableIntent`s.
    return {
        storefront: Bag.countryCode(objectGraph),
        language: null,
    };
}
